/**
 * Background activity logging (PRD §5.F, BR-13, D-45) — POST /activity/events/.
 *
 * - Never blocks or changes the UI: track() returns immediately, errors are swallowed.
 * - Events are queued on the device and sent in batches (≤ 25, every 5 s, and when the page hides).
 *   A batch shares device_id / session_id / town, so consecutive events with the same session and
 *   town go together.
 * - Immediate kinds (WhatsApp, call, directions taps) are flushed at once with sendBeacon so they
 *   survive the jump to WhatsApp / the dialer.
 * - Unsent events stay in localStorage and are retried later; each carries a client_event_id so
 *   the server ignores duplicates.
 * - Bodies go as text/plain (no CORS preflight; a beacon can't send application/json).
 */
import { buildUrl } from '@/lib/api'
import { safeStorage } from '@/lib/storage'
import { istIso } from '@/lib/time'
import { uuid } from '@/lib/uuid'
import type { ActivityBatch, ActivityEvent, ActivityKind } from '@/types/api'

import { getDeviceId } from '../device/deviceId'
import { campaignField } from './campaign'
import { sharedLocation } from '../location/store'
import { touchSession } from '../device/session'
import { useTownStore } from '../town/store'

const QUEUE_KEY = 'angadi.activity_queue'
export const MAX_BATCH = 25
const MAX_QUEUE = 300 // drop oldest beyond this (offline for a long time)
const FLUSH_MS = 5000

export const IMMEDIATE: ReadonlySet<ActivityKind> = new Set([
  'whatsapp_tap',
  'call_tap',
  'directions_tap',
])

export type EventInput = Omit<ActivityEvent, 'client_event_id' | 'client_ts' | 'kind'>

/** A queued event plus the batch-level fields it was recorded with. */
interface Queued {
  event: ActivityEvent
  session_id: string | null
  town: string | null
}

function readQueue(): Queued[] {
  try {
    const q = JSON.parse(safeStorage.get(QUEUE_KEY) ?? '[]') as unknown
    return Array.isArray(q) ? (q as Queued[]).filter((x) => x && x.event) : []
  } catch {
    return []
  }
}

function writeQueue(items: Queued[]): void {
  safeStorage.set(QUEUE_KEY, JSON.stringify(items.slice(-MAX_QUEUE)))
}

/** Build a full queued event (ids, session, town, client time). Exported for tests. */
export function buildEvent(kind: ActivityKind, data: EventInput = {}): Queued {
  return {
    event: { client_event_id: uuid(), kind, client_ts: istIso(), ...campaignField(), ...data },
    session_id: touchSession().id,
    town: useTownStore.getState().town,
  }
}

/** Head of the queue that can share one batch body (same session + town, ≤ 25). */
export function takeBatch(queue: Queued[]): Queued[] {
  const first = queue[0]
  if (!first) return []
  const batch: Queued[] = []
  for (const q of queue) {
    if (batch.length >= MAX_BATCH || q.session_id !== first.session_id || q.town !== first.town) {
      break
    }
    batch.push(q)
  }
  return batch
}

function body(batch: Queued[]): string {
  const b: ActivityBatch = {
    device_id: getDeviceId(),
    session_id: batch[0]?.session_id ?? null,
    town: batch[0]?.town ?? null,
    location: sharedLocation(), // latest shared position, saved on today's visit (D-77)
    events: batch.map((q) => q.event),
  }
  return JSON.stringify(b)
}

/** Send one batch. Resolves true when it can leave the queue; never throws. */
async function send(batch: Queued[], useBeacon: boolean): Promise<boolean> {
  if (import.meta.env.VITE_DEMO === '1') return true // the demo logs nothing: drop the batch
  const payload = body(batch)
  const url = buildUrl('/activity/events/')
  try {
    if (
      useBeacon &&
      typeof navigator !== 'undefined' &&
      navigator.sendBeacon?.(url, new Blob([payload], { type: 'text/plain' }))
    ) {
      return true
    }
    const res = await fetch(url, {
      method: 'POST',
      body: payload,
      keepalive: true,
      headers: { 'Content-Type': 'text/plain' },
    })
    // 4xx other than 429 will never succeed: drop the batch instead of retrying forever.
    return res.ok || (res.status >= 400 && res.status < 500 && res.status !== 429)
  } catch {
    return false
  }
}

let flushing = false

/** Callbacks run before the hide-flush (e.g. pending `seen` ids). */
const beforeHide: (() => void)[] = []
export function onBeforeHide(fn: () => void): void {
  beforeHide.push(fn)
}

/** Send queued events in batches; keep whatever fails for the next attempt. */
export async function flush({ beacon = false } = {}): Promise<void> {
  // A periodic fetch may be in flight: an immediate tap or the hide-flush must still go out now.
  // Sending an event twice is harmless; the server ignores repeated client_event_ids (BR-13).
  if (flushing && !beacon) return
  const owner = !flushing
  flushing = true
  try {
    let queue = readQueue()
    while (queue.length) {
      const batch = takeBatch(queue)
      if (!(await send(batch, beacon))) break
      const sent = new Set(batch.map((q) => q.event.client_event_id))
      queue = readQueue().filter((q) => !sent.has(q.event.client_event_id))
      writeQueue(queue)
    }
  } catch {
    /* never affects the user */
  } finally {
    if (owner) flushing = false
  }
}

/** Fire-and-forget. Safe to call anywhere, including right before opening WhatsApp. */
export function track(kind: ActivityKind, data: EventInput = {}): void {
  try {
    writeQueue([...readQueue(), buildEvent(kind, data)])
    if (IMMEDIATE.has(kind)) void flush({ beacon: true })
  } catch {
    /* logging must never affect the user */
  }
}

/** Call once at startup: periodic flush and flush on hide / back online (+ `onHide` hooks). */
export function startTracker(): () => void {
  const timer = window.setInterval(() => void flush(), FLUSH_MS)
  const onHide = () => {
    if (document.visibilityState !== 'hidden') return
    for (const fn of beforeHide) fn()
    void flush({ beacon: true })
  }
  const onOnline = () => void flush()
  document.addEventListener('visibilitychange', onHide)
  window.addEventListener('online', onOnline)
  return () => {
    window.clearInterval(timer)
    document.removeEventListener('visibilitychange', onHide)
    window.removeEventListener('online', onOnline)
  }
}
